import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePageTitle } from '../../hooks/usePageTitle'
import { usersApi, type User } from '../../api/settings'
import { organizationsApi, type Organization } from '../../api/contacts'
import { callConfigsApi } from '../../api/callConfigs'
import { useAuthStore } from '../../store/auth'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PageSpinner } from '../../components/ui/Spinner'
import { formatDate, cn, fullName } from '../../lib/utils'
import { Plus, UserX, UserCheck, Pencil, PhoneOff, Copy } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { DropdownSelect } from '../../components/ui/DropdownSelect'

interface CreateUserFormData {
  firstName: string; lastName: string; email: string; password: string; role: string
  callExtension?: string; callSipUsername?: string; callSipPassword?: string
  callPbxWsUri?: string; callPbxHost?: string; callDisplayName?: string
}

interface EditUserFormData {
  firstName: string; lastName: string; email: string
  canSeePhone?: boolean; canSeeEmail?: boolean; canSeeWhatsapp?: boolean; canSeeRecordings?: boolean; canEditServicePrices?: boolean
  callExtension?: string; callSipUsername?: string; callSipPassword?: string
  callPbxWsUri?: string; callPbxHost?: string; callDisplayName?: string; callEnabled?: boolean
}

export function UsersSettings() {
  usePageTitle('Users')
  const { companyId } = useParams<{ companyId: string }>()
  const { user: authUser } = useAuthStore()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editTarget, setEditTarget] = useState<User | null>(null)
  const [deactivateTarget, setDeactivateTarget] = useState<{ id: string; label: string } | null>(null)

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', companyId],
    queryFn: () => usersApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const { data: orgs } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId!, { limit: 500 }).then(r => r.data.data),
    enabled: !!companyId,
  })
  const orgMap = new Map((orgs ?? []).map((o: Organization) => [o.id, o.name]))

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => usersApi.deactivate(companyId!, id),
    onSuccess: () => {
      toast.success('User deactivated')
      qc.invalidateQueries({ queryKey: ['users', companyId] })
      setDeactivateTarget(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to deactivate user')
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => usersApi.update(companyId!, id, { isActive: true }),
    onSuccess: () => {
      toast.success('User reactivated')
      qc.invalidateQueries({ queryKey: ['users', companyId] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to reactivate user')
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Users</h2>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
          <Plus className="w-3.5 h-3.5" /> Add User
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Campaigns</th><th>Status</th><th>Joined</th><th></th></tr>
          </thead>
          <tbody>
            {(!users || users.length === 0) && (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500 text-sm">No users yet</td></tr>
            )}
            {users?.map((u: User) => (
              <tr key={u.id}>
                <td className="font-medium">{fullName(u.firstName, u.lastName)}</td>
                <td className="text-gray-600">{u.email}</td>
                <td><span className="badge badge-blue">{u.role}</span></td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(u.organizationIds ?? []).map(id => orgMap.has(id) && (
                      <span key={id} className="badge badge-purple text-xs">{orgMap.get(id)}</span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`badge ${u.isActive ? 'badge-green' : 'badge-gray'}`}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="text-gray-500 text-xs">{formatDate(u.createdAt)}</td>
                <td className="flex items-center gap-1">
                  <button
                    onClick={() => setEditTarget(u)}
                    className="btn btn-ghost btn-sm p-1"
                    aria-label="Edit user"
                    title="Edit user"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {u.isActive ? (
                    <button
                      onClick={() => setDeactivateTarget({ id: u.id, label: fullName(u.firstName, u.lastName) })}
                      className="btn btn-ghost btn-sm text-red-600 p-1"
                      aria-label="Deactivate user"
                      title="Deactivate user"
                    >
                      <UserX className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => reactivateMutation.mutate(u.id)}
                      disabled={reactivateMutation.isPending}
                      className="btn btn-ghost btn-sm text-green-600 p-1"
                      aria-label="Reactivate user"
                      title="Reactivate user"
                    >
                      <UserCheck className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add User" size="sm">
        <AddUserForm
          companyId={companyId!}
          orgs={orgs ?? []}
          onSuccess={() => {
            setShowCreate(false)
            qc.invalidateQueries({ queryKey: ['users', companyId] })
          }}
        />
      </Modal>

      {editTarget && (
        <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title={`Edit — ${fullName(editTarget.firstName, editTarget.lastName)}`} size="sm">
          <EditUserForm
            companyId={companyId!}
            user={editTarget}
            orgs={orgs ?? []}
            isAdmin={authUser?.role === 'administrator'}
            canManageAccess={authUser?.role === 'administrator' || authUser?.role === 'company_owner'}
            onSuccess={() => {
              setEditTarget(null)
              qc.invalidateQueries({ queryKey: ['users', companyId] })
            }}
          />
        </Modal>
      )}

      <ConfirmDialog
        isOpen={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
        title="Deactivate user?"
        message={`${deactivateTarget?.label} will no longer be able to sign in. They stay listed here as Inactive and can be reactivated later.`}
        confirmLabel="Deactivate"
        isPending={deactivateMutation.isPending}
      />
    </div>
  )
}

function EditUserForm({ companyId, user, orgs, isAdmin, canManageAccess, onSuccess }: {
  companyId: string; user: User; orgs: Organization[]; isAdmin: boolean; canManageAccess?: boolean; onSuccess: () => void
}) {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<EditUserFormData>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      canSeePhone: user.canSeePhone ?? false,
      canSeeEmail: user.canSeeEmail ?? false,
      canSeeWhatsapp: user.canSeeWhatsapp ?? false,
      canSeeRecordings: user.canSeeRecordings ?? false,
      canEditServicePrices: user.canEditServicePrices ?? false,
    },
  })
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>(user.organizationIds ?? [])
  const toggleOrg = (id: string) =>
    setSelectedOrgIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const qc = useQueryClient()

  // Load existing call config (admin only)
  const { data: existingConfig, isLoading: loadingConfig } = useQuery({
    queryKey: ['call-config-admin', companyId, user.id],
    queryFn: () => callConfigsApi.getForAdmin(companyId, user.id).then(r => r.data as any).catch(() => null),
    enabled: isAdmin,
  })

  // Fallback: company PBX defaults when user has no config yet
  const { data: defaults } = useQuery({
    queryKey: ['call-config-defaults', companyId],
    queryFn: () => callConfigsApi.getDefaults(companyId).then(r => r.data),
    enabled: isAdmin && existingConfig === null,
  })

  // Pre-fill call config fields once loaded
  useEffect(() => {
    if (existingConfig) {
      setValue('callExtension', existingConfig.extension ?? '')
      setValue('callSipUsername', existingConfig.sipUsername ?? '')
      setValue('callSipPassword', existingConfig.sipPassword ?? '')
      setValue('callPbxWsUri', existingConfig.pbxWsUri ?? '')
      setValue('callPbxHost', existingConfig.pbxHost ?? '')
      setValue('callDisplayName', existingConfig.displayName ?? '')
      setValue('callEnabled', existingConfig.isEnabled ?? true)
    } else if (existingConfig === null && defaults) {
      setValue('callPbxWsUri', defaults.pbxWsUri ?? '')
      setValue('callPbxHost', defaults.pbxHost ?? '')
      setValue('callDisplayName', `${user.firstName}'s Phone`)
    }
  }, [existingConfig, defaults])

  const removeConfigMutation = useMutation({
    mutationFn: () => callConfigsApi.remove(companyId, user.id),
    onSuccess: () => {
      toast.success('Call configuration removed')
      qc.invalidateQueries({ queryKey: ['call-config-admin', companyId, user.id] })
    },
    onError: () => toast.error('Failed to remove call configuration'),
  })

  const mutation = useMutation({
    mutationFn: async (dto: EditUserFormData) => {
      await usersApi.update(companyId, user.id, {
        firstName: dto.firstName,
        lastName: dto.lastName,
        email: dto.email,
        organizationIds: selectedOrgIds,
        ...(canManageAccess && user.role === 'sales_user'
          ? { canSeePhone: dto.canSeePhone ?? false, canSeeEmail: dto.canSeeEmail ?? false, canSeeWhatsapp: dto.canSeeWhatsapp ?? false, canSeeRecordings: dto.canSeeRecordings ?? false, canEditServicePrices: dto.canEditServicePrices ?? false }
          : {}),
      })
      if (isAdmin && dto.callExtension && dto.callSipUsername && dto.callPbxWsUri && dto.callPbxHost) {
        await callConfigsApi.upsert(companyId, user.id, {
          extension: dto.callExtension,
          sipUsername: dto.callSipUsername,
          ...(dto.callSipPassword ? { sipPassword: dto.callSipPassword } : {}),
          pbxWsUri: dto.callPbxWsUri,
          pbxHost: dto.callPbxHost,
          displayName: dto.callDisplayName || undefined,
          isEnabled: dto.callEnabled ?? true,
        })
      }
    },
    onSuccess: () => { toast.success('User updated'); onSuccess() },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to update user')
    },
  })

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">First Name *</label>
          <input {...register('firstName', { required: true })} className={cn('input', errors.firstName && 'border-red-400 focus:border-red-400 focus:ring-red-100')} />
          {errors.firstName && <p className="text-xs text-red-500 mt-0.5">Required</p>}
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input {...register('lastName', { required: true })} className={cn('input', errors.lastName && 'border-red-400 focus:border-red-400 focus:ring-red-100')} />
          {errors.lastName && <p className="text-xs text-red-500 mt-0.5">Required</p>}
        </div>
      </div>
      <div>
        <label className="label">Email *</label>
        <input {...register('email', { required: true })} type="email" className={cn('input', errors.email && 'border-red-400 focus:border-red-400 focus:ring-red-100')} />
        {errors.email && <p className="text-xs text-red-500 mt-0.5">Required</p>}
      </div>

      {orgs.length > 0 && (
        <div>
          <label className="label">Campaigns / Organizations</label>
          <div className="border border-gray-200 rounded-md p-2 max-h-36 overflow-y-auto space-y-1">
            {orgs.map(o => (
              <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                <input
                  type="checkbox"
                  checked={selectedOrgIds.includes(o.id)}
                  onChange={() => toggleOrg(o.id)}
                  className="w-3.5 h-3.5"
                />
                <span className="flex items-center gap-1.5">
                  {o.color && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: o.color }} />}
                  {o.name}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {canManageAccess && user.role === 'sales_user' && (
        <div className="border-t border-gray-200 pt-3 mt-1">
          <p className="text-xs font-medium text-gray-700 mb-2">Data Access</p>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input {...register('canSeePhone')} type="checkbox" className="w-3.5 h-3.5" />
              <span>Allow this user to see phone numbers</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input {...register('canSeeEmail')} type="checkbox" className="w-3.5 h-3.5" />
              <span>Allow this user to see email addresses</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input {...register('canSeeWhatsapp')} type="checkbox" className="w-3.5 h-3.5" />
              <span>Allow this user to see WhatsApp numbers</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input {...register('canSeeRecordings')} type="checkbox" className="w-3.5 h-3.5" />
              <span>Allow this user to listen to call recordings</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input {...register('canEditServicePrices')} type="checkbox" className="w-3.5 h-3.5" />
              Can edit service prices on proformas
            </label>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="border-t border-gray-200 pt-3 mt-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-700">Call Configuration</p>
            {existingConfig && !loadingConfig && (
              <button
                type="button"
                onClick={() => removeConfigMutation.mutate()}
                disabled={removeConfigMutation.isPending}
                className="btn btn-ghost btn-sm text-red-600 text-xs p-1 h-auto flex items-center gap-1"
              >
                <PhoneOff className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
          {loadingConfig ? (
            <p className="text-xs text-gray-400">Loading...</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label">Extension</label><input {...register('callExtension')} className="input" placeholder="e.g. 101" /></div>
                <div><label className="label">SIP Username</label><input {...register('callSipUsername')} className="input" placeholder="e.g. 101" /></div>
              </div>
              <div>
                <label className="label">SIP Password</label>
                <div className="relative">
                  <input {...register('callSipPassword')} type="password" className="input pr-9" placeholder={existingConfig ? 'Leave blank to keep current' : 'Required for new config'} />
                  <button
                    type="button"
                    onClick={() => { const v = watch('callSipPassword'); if (v) { navigator.clipboard.writeText(v); toast.success('Copied') } }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Copy password"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div><label className="label">PBX WebSocket URI</label><input {...register('callPbxWsUri')} className="input" placeholder="wss://host:8089/ws" /></div>
              <div><label className="label">PBX Host</label><input {...register('callPbxHost')} className="input" placeholder="e.g. 84.247.177.107" /></div>
              <div><label className="label">Display Name</label><input {...register('callDisplayName')} className="input" placeholder="e.g. John Doe" /></div>
              <div className="flex items-center gap-2">
                <input {...register('callEnabled')} type="checkbox" id="callEnabled" className="w-4 h-4" defaultChecked />
                <label htmlFor="callEnabled" className="label mb-0 cursor-pointer">Enabled</label>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Save Changes</button>
      </div>
    </form>
  )
}

function AddUserForm({ companyId, orgs, onSuccess }: { companyId: string; orgs: Organization[]; onSuccess: () => void }) {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'administrator'
  const { register, handleSubmit, reset, getValues, setValue, watch, formState: { errors } } = useForm<CreateUserFormData>({
    defaultValues: { role: 'sales_user' },
  })
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([])
  const [orgError, setOrgError] = useState(false)
  const toggleOrg = (id: string) => {
    setSelectedOrgIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    setOrgError(false)
  }

  const { data: defaults } = useQuery({
    queryKey: ['call-config-defaults', companyId],
    queryFn: () => callConfigsApi.getDefaults(companyId).then(r => r.data),
    enabled: user?.role === 'administrator',
  })

  useEffect(() => {
    if (defaults) {
      if (!getValues('callPbxWsUri')) setValue('callPbxWsUri', defaults.pbxWsUri)
      if (!getValues('callPbxHost')) setValue('callPbxHost', defaults.pbxHost)
    }
  }, [defaults])

  // Auto-fill display name as "{firstName}'s Phone" unless manually overridden
  const firstName = watch('firstName', '')
  useEffect(() => {
    const current = getValues('callDisplayName') ?? ''
    if (!current || current.endsWith("'s Phone")) {
      setValue('callDisplayName', firstName ? `${firstName}'s Phone` : '')
    }
  }, [firstName])

  const mutation = useMutation({
    mutationFn: async (dto: CreateUserFormData) => {
      const role = isAdmin ? dto.role : 'sales_user'
      if (selectedOrgIds.length === 0) {
        setOrgError(true)
        throw new Error('At least one organization is required')
      }
      const userRes = await usersApi.create(companyId, {
        firstName: dto.firstName, lastName: dto.lastName,
        email: dto.email,
        ...(dto.password ? { password: dto.password } : {}),
        role,
        organizationIds: selectedOrgIds,
      })
      const newUserId = userRes.data.id
      if (dto.callExtension && dto.callSipUsername && dto.callSipPassword && dto.callPbxWsUri && dto.callPbxHost) {
        await callConfigsApi.upsert(companyId, newUserId, {
          extension: dto.callExtension,
          sipUsername: dto.callSipUsername,
          sipPassword: dto.callSipPassword,
          pbxWsUri: dto.callPbxWsUri,
          pbxHost: dto.callPbxHost,
          displayName: dto.callDisplayName || undefined,
        })
      }
      return userRes
    },
    onSuccess: (_data, dto) => {
      reset(); setSelectedOrgIds([]); setOrgError(false)
      toast.success(dto.password ? 'User created' : 'Invitation sent')
      onSuccess()
    },
    onError: (err: any) => {
      if (err?.message === 'At least one organization is required') return
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to create user')
    },
  })

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">First Name *</label>
          <input {...register('firstName', { required: true })} className={cn('input', errors.firstName && 'border-red-400 focus:border-red-400 focus:ring-red-100')} />
          {errors.firstName && <p className="text-xs text-red-500 mt-0.5">Required</p>}
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input {...register('lastName', { required: true })} className={cn('input', errors.lastName && 'border-red-400 focus:border-red-400 focus:ring-red-100')} />
          {errors.lastName && <p className="text-xs text-red-500 mt-0.5">Required</p>}
        </div>
      </div>
      <div>
        <label className="label">Email *</label>
        <input {...register('email', { required: true })} type="email" className={cn('input', errors.email && 'border-red-400 focus:border-red-400 focus:ring-red-100')} />
        {errors.email && <p className="text-xs text-red-500 mt-0.5">Required</p>}
      </div>
      <div>
        <label className="label">Password <span className="text-gray-400 font-normal">(optional)</span></label>
        <input {...register('password')} type="password" className="input" placeholder="Leave blank to send invite email" />
        <p className="text-xs text-gray-500 mt-0.5">Leave blank to send an invitation link instead.</p>
      </div>
      {isAdmin && (
        <div>
          <label className="label">Role</label>
          <DropdownSelect
            value={watch('role') ?? 'sales_user'}
            onChange={v => setValue('role', v as 'sales_user' | 'company_owner')}
            options={[{ value: 'sales_user', label: 'Sales User' }, { value: 'company_owner', label: 'Company Owner' }]}
          />
        </div>
      )}
      <div>
        <label className="label">Campaigns / Organizations *</label>
        {orgs.length === 0 ? (
          <p className="text-xs text-gray-500">No organizations yet — create one first.</p>
        ) : (
          <div className={cn('border rounded-md p-2 max-h-36 overflow-y-auto space-y-1', orgError ? 'border-red-400' : 'border-gray-200')}>
            {orgs.map(o => (
              <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                <input
                  type="checkbox"
                  checked={selectedOrgIds.includes(o.id)}
                  onChange={() => toggleOrg(o.id)}
                  className="w-3.5 h-3.5"
                />
                <span className="flex items-center gap-1.5">
                  {o.color && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: o.color }} />}
                  {o.name}
                </span>
              </label>
            ))}
          </div>
        )}
        {orgError && <p className="text-xs text-red-500 mt-0.5">At least one organization is required</p>}
      </div>
      {isAdmin && (
        <div className="border-t border-gray-200 pt-3 mt-1">
          <p className="text-xs font-medium text-gray-700 mb-2">Call Configuration (Optional)</p>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="label">Extension</label><input {...register('callExtension')} className="input" placeholder="e.g. 101" /></div>
              <div><label className="label">SIP Username</label><input {...register('callSipUsername')} className="input" placeholder="e.g. 101" /></div>
            </div>
            <div><label className="label">SIP Password</label><input {...register('callSipPassword')} type="password" className="input" /></div>
            <div><label className="label">PBX WebSocket URI</label><input {...register('callPbxWsUri')} className="input" placeholder="wss://host:8089/ws" /></div>
            <div><label className="label">PBX Host</label><input {...register('callPbxHost')} className="input" placeholder="e.g. 84.247.177.107" /></div>
            <div><label className="label">Display Name</label><input {...register('callDisplayName')} className="input" placeholder="e.g. John Doe" /></div>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Create User</button>
      </div>
    </form>
  )
}
